import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
} from '@mui/icons-material'
import { educationModulesAPI, educationDraftsAPI } from '../services/api'

const STATUS_LABELS = {
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  analyzing: 'Analyzing',
  analysis_failed: 'Failed',
  draft_ready: 'Ready',
  editing: 'Editing',
  ingesting: 'Ingesting',
  ingestion_failed: 'Failed',
  completed: 'Completed',
  published: 'Published',
  draft: 'Draft',
  archived: 'Archived',
}

// Status indicator styles
const statusStyles = {
  draft: { color: '#71717a' },
  published: { color: '#16a34a' },
  archived: { color: '#71717a' },
  uploading: { color: '#0284c7' },
  uploaded: { color: '#0284c7' },
  analyzing: { color: '#ca8a04' },
  analysis_failed: { color: '#dc2626' },
  draft_ready: { color: '#0f766e' },
  editing: { color: '#0f766e' },
  ingesting: { color: '#ca8a04' },
  ingestion_failed: { color: '#dc2626' },
  completed: { color: '#16a34a' },
}

function StatusIndicator({ status }) {
  const style = statusStyles[status] || { color: '#71717a' }
  const label = STATUS_LABELS[status] || status
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {label}
      </Typography>
    </Box>
  )
}

function EducationModules() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [modules, setModules] = useState([])
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: null })
  const [actionLoading, setActionLoading] = useState(null)

  const fetchModules = useCallback(async () => {
    try {
      const data = await educationModulesAPI.getAll()
      setModules(data)
    } catch (err) {
      console.error('Error fetching modules:', err)
      setError('Failed to load education modules')
    }
  }, [])

  const fetchDrafts = useCallback(async () => {
    try {
      const data = await educationDraftsAPI.getAll()
      setDrafts(data)
    } catch (err) {
      console.error('Error fetching drafts:', err)
      setError('Failed to load drafts')
    }
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    await Promise.all([fetchModules(), fetchDrafts()])
    setLoading(false)
  }, [fetchModules, fetchDrafts])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const handleCreateNew = async () => {
    try {
      setActionLoading('create')
      const draft = await educationDraftsAPI.create()
      navigate(`/education-modules/builder/${draft.draft_id}`)
    } catch (err) {
      if (err.response?.status === 409) {
        // Already have an active draft
        const activeDraftId = err.response.data.active_draft_id
        if (activeDraftId) {
          navigate(`/education-modules/builder/${activeDraftId}`)
        } else {
          setError('You already have an active draft in progress')
        }
      } else {
        setError('Failed to create new draft: ' + (err.response?.data?.error || err.message))
      }
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteModule = async () => {
    if (!deleteDialog.item) return
    try {
      setActionLoading(`delete-${deleteDialog.item.module_id}`)
      await educationModulesAPI.delete(deleteDialog.item.module_id)
      await fetchModules()
      setDeleteDialog({ open: false, item: null, type: null })
    } catch (err) {
      setError('Failed to delete module: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteDraft = async () => {
    if (!deleteDialog.item) return
    try {
      setActionLoading(`delete-${deleteDialog.item.draft_id}`)
      await educationDraftsAPI.delete(deleteDialog.item.draft_id)
      await fetchDrafts()
      setDeleteDialog({ open: false, item: null, type: null })
    } catch (err) {
      setError('Failed to delete draft: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  const handlePublishModule = async (moduleId) => {
    try {
      setActionLoading(`publish-${moduleId}`)
      await educationModulesAPI.publish(moduleId)
      await fetchModules()
    } catch (err) {
      setError('Failed to publish module: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const renderModulesTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Module ID</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Chapters</TableCell>
            <TableCell>Lessons</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {modules.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Box sx={{ py: 4 }}>
                  <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No education modules yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create your first module using the button above
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            modules.map((module) => (
              <TableRow key={module.id} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {module.module_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {module.title}
                  </Typography>
                </TableCell>
                <TableCell>{module.version}</TableCell>
                <TableCell>{module.chapter_count || 0}</TableCell>
                <TableCell>{module.lesson_count || 0}</TableCell>
                <TableCell>
                  <StatusIndicator status={module.status} />
                </TableCell>
                <TableCell>{formatDate(module.updated_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/education-modules/${module.module_id}`)}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {module.status !== 'published' && (
                    <Tooltip title="Publish">
                      <IconButton
                        size="small"
                        onClick={() => handlePublishModule(module.module_id)}
                        disabled={actionLoading === `publish-${module.module_id}`}
                      >
                        {actionLoading === `publish-${module.module_id}` ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PublishIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, item: module, type: 'module' })}
                      disabled={actionLoading === `delete-${module.module_id}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const renderDraftsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Draft ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Files</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Box sx={{ py: 4 }}>
                  <Typography color="text.secondary">No drafts in progress</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            drafts.map((draft) => (
              <TableRow key={draft.id} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {draft.draft_id}
                  </Typography>
                  {draft.module_id && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Module: {draft.module_id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StatusIndicator status={draft.status} />
                  {draft.current_step && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {draft.current_step}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={draft.progress_percent || 0}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="caption">{draft.progress_percent || 0}%</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {draft.source_files?.length || 0} files
                </TableCell>
                <TableCell>{draft.created_by_name || '-'}</TableCell>
                <TableCell>{formatDate(draft.updated_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Continue Editing">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/education-modules/builder/${draft.draft_id}`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, item: draft, type: 'draft' })}
                      disabled={actionLoading === `delete-${draft.draft_id}` || draft.status === 'published'}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5">Education Modules</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage patient education content and modules.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={actionLoading === 'create' ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleCreateNew}
            disabled={actionLoading === 'create'}
          >
            Create Module
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Published Modules (${modules.filter(m => m.status === 'published').length})`} />
          <Tab label={`Drafts (${drafts.length})`} />
          <Tab label={`All Modules (${modules.length})`} />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && renderModulesTable()}
          {activeTab === 1 && renderDraftsTable()}
          {activeTab === 2 && renderModulesTable()}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null, type: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteDialog.type === 'module' ? (
              <>
                Are you sure you want to delete the module{' '}
                <strong>{deleteDialog.item?.title || deleteDialog.item?.module_id}</strong>?
                This will archive the module and remove it from active use.
              </>
            ) : (
              <>
                Are you sure you want to delete this draft?
                This will permanently remove the draft and all uploaded files.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null, type: null })}>
            Cancel
          </Button>
          <Button
            onClick={deleteDialog.type === 'module' ? handleDeleteModule : handleDeleteDraft}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EducationModules
